import type { GlucoseActivity } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActivityType,
  CGMType,
  DataSource,
} from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import { useMemo } from "react"
import { useScrollPositionContext } from "../ScrollPositionContext"
import { useVirtualListContext } from "../VirtualListContext"
import { useXAxisContext } from "../XAxisContext"
import {
  useFollowedUsers,
  usePolyglot,
} from "src/contexts"
import { useEventsAndSubEntriesOfType } from "src/hooks"
import { createStartedAtRangeSelector } from "src/utils"

const prefix = "pages.home.graph"
const dexcomCgmTypes: GlucoseActivity["cgm_type"][] = [
  CGMType.DexcomG4,
  CGMType.DexcomG5,
  CGMType.DexcomG6,
  CGMType.DexcomG6Plus,
  CGMType.DexcomG7,
  CGMType.DexcomPro,
]
const abbottCgmTypes: GlucoseActivity["cgm_type"][] = [
  CGMType.AbbottFreeStyleLibre2,
  CGMType.AbbottFreeStyleLibre3,
]
export function useDataAttributionInGraph(): string | undefined {
  const polyglot = usePolyglot()
  const { scrollPosition } = useScrollPositionContext()
  const { containerBoundingClientRect } = useVirtualListContext()
  const containerWidth = containerBoundingClientRect.width

  const {
    cellWidth,
    graphStart,
    minutesPerCell,
  } = useXAxisContext()

  const selector = useMemo(
    () => {
      if (!containerWidth) {
        return {}
      }

      const pixelsPerMinute = cellWidth / minutesPerCell
      const durationInHours = containerWidth / (pixelsPerMinute * 60)
      // find the time at the right edge of the graph
      const newestTimeInGraph = dayjs(graphStart)
        .subtract(
          scrollPosition / pixelsPerMinute,
          "minutes",
        )
        .toISOString()
      return createStartedAtRangeSelector(
        newestTimeInGraph,
        durationInHours,
      )
    },
    [
      cellWidth,
      minutesPerCell,
      scrollPosition,
      graphStart,
      containerWidth,
    ],
  )

  const { currentDeviceSettings } = useFollowedUsers()

  const {
    events,
  } = useEventsAndSubEntriesOfType(
    currentDeviceSettings.following_account_id,
    ActivityType.Glucose,
    selector,
  )

  // find last Dexcom event
  const dexcomEvent = useMemo(
    () => events.find(
      (glucoseEvent) => glucoseEvent.source === DataSource.Dexcom ||
    dexcomCgmTypes.includes(glucoseEvent.glucose.cgm_type),
    ),
    [events],
  )

  // find last Abbott FreeStyle Libre event
  const abbottEvent = useMemo(
    () => events.find((glucoseEvent) => abbottCgmTypes.includes(glucoseEvent.glucose.cgm_type as CGMType)),
    [events],
  )

  if (dexcomEvent && abbottEvent) {
    const dexcomLabel = polyglot.t(`${prefix}.tooltip.source.dexcom`)
    const abbottLabel = polyglot.t(`${prefix}.tooltip.cgm_type.${abbottEvent.glucose.cgm_type}`)
    const [
      source1,
      source2,
    ] = dexcomEvent.started_at < abbottEvent.started_at
      ? [
        dexcomLabel,
        abbottLabel,
      ]
      : [
        abbottLabel,
        dexcomLabel,
      ]

    return polyglot.t(
      `${prefix}.data_attribution_multiple_sources`,
      {
        source1,
        source2,
      },
    )
  }

  if (dexcomEvent || abbottEvent) {
    const source = dexcomEvent
      ? polyglot.t(`${prefix}.tooltip.source.dexcom`)
      : polyglot.t(`${prefix}.tooltip.cgm_type.${abbottEvent!.glucose.cgm_type}`)

    return polyglot.t(
      `${prefix}.data_attribution_single_source`,
      { source },
    )
  }

  return undefined
}
